import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
} from '@mui/material'
import { Home, Search, ArrowBack, Help, ContactSupport } from '@mui/icons-material'
import Layout from '../components/Layout'
import { useTranslation } from 'react-i18next'
import { useColorMode } from '../theme'

const NotFound: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mode } = useColorMode()
  const isDark = mode === 'dark'

  const handleGoHome = () => {
    navigate('/')
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleSearch = () => {
    navigate('/leistungen')
  }

  const handleContact = () => {
    navigate('/kontakt')
  }

  const helpfulLinks = [
    {
      title: t('home'),
      description: t('goToHome'),
      icon: <Home />,
      action: handleGoHome,
    },
    {
      title: t('services'),
      description: t('exploreServices'),
      icon: <Search />,
      action: handleSearch,
    },
    {
      title: t('contact'),
      description: t('getHelpSupport'),
      icon: <ContactSupport />,
      action: handleContact,
    },
  ]

  return (
    <Layout>
      <Box
        sx={{
          background: isDark
            ? `radial-gradient(circle at top, ${alpha(theme.palette.primary.main, 0.25)}, rgba(15, 23, 42, 0.95) 55%)`
            : `radial-gradient(circle at top, ${alpha(theme.palette.primary.main, 0.12)}, rgba(255, 255, 255, 0.95) 60%)`,
          minHeight: 'calc(100vh - 200px)',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 8, sm: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 5,
              background: isDark
                ? alpha('#0f172a', 0.8)
                : alpha(theme.palette.common.white, 0.86),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.8),
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                    }}
                  >
                    404
                  </Typography>
                  <Typography
                    variant="h3"
                    component="h1"
                    fontWeight={800}
                    sx={{
                      lineHeight: 1.1,
                      color: isDark ? '#ffffff' : theme.palette.text.primary,
                    }}
                  >
                    {t('notFoundTitle')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDark ? alpha('#ffffff', 0.85) : alpha('#0f172a', 0.75),
                      lineHeight: 1.6,
                    }}
                  >
                    {t('notFoundSubtitle')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      onClick={handleGoHome}
                      variant="contained"
                      startIcon={<Home />}
                      sx={{ minWidth: 180 }}
                    >
                      {t('home')}
                    </Button>
                    <Button
                      onClick={handleGoBack}
                      variant="text"
                      startIcon={<ArrowBack />}
                      sx={{
                        color: isDark ? '#ffffff' : theme.palette.primary.main,
                      }}
                    >
                      {t('back')}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: isDark ? alpha('#ffffff', 0.9) : alpha('#0f172a', 0.85),
                    }}
                  >
                    <Help fontSize="small" />
                    {t('maybeLookingFor')}
                  </Typography>
                  {helpfulLinks.map((item) => (
                    <Card
                      key={item.title}
                      elevation={0}
                      sx={{
                        borderRadius: 4,
                        background: isDark
                          ? `linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.85) 100%)`
                          : `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.95) 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                        boxShadow: 'none',
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          py: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: isDark ? '#ffffff' : theme.palette.primary.main,
                              fontWeight: 700,
                              mb: 0.5,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? alpha('#ffffff', 0.85) : alpha('#0f172a', 0.7),
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                        <Button
                          onClick={item.action}
                          variant="outlined"
                          color="primary"
                          sx={{ minWidth: 120 }}
                        >
                          {t('open')}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </Layout>
  )
}

export default NotFound
